import {
  clearAuthSession,
  clearExpiredAuthSession,
  getAuthToken,
} from "../../../utils/authSession";

const getBaseUrl = () => {
  const raw = process.env.REACT_APP_LEARNHUT_AI_URL || "";
  return raw.replace(/\/+$/, "");
};

const subjectPath = (aiSubjectId, endpoint) =>
  `/subjects/${encodeURIComponent(aiSubjectId)}/${endpoint}`;

const parseResponse = async (response) => {
  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { message: text };
    }
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      clearAuthSession();
    }
    const detail = data?.detail || data?.message || data?.error || response.statusText;
    throw new Error(Array.isArray(detail) ? detail.map((item) => item?.msg || item).join(", ") : detail);
  }

  return data || {};
};

const getAuthorizedBase = () => {
  const baseUrl = getBaseUrl();
  if (!baseUrl) {
    throw new Error("LearnHut AI URL fehlt. Setze REACT_APP_LEARNHUT_AI_URL.");
  }

  const token = getAuthToken();
  if (!token) {
    clearExpiredAuthSession();
    throw new Error("Du bist nicht angemeldet oder deine Sitzung ist abgelaufen.");
  }

  return { baseUrl, token };
};

const requestJson = async (path, payload) => {
  const { baseUrl, token } = getAuthorizedBase();

  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload || {}),
  });

  return parseResponse(response);
};

const appendFormValue = (formData, key, value) => {
  if (value === undefined || value === null || value === "") return;
  formData.append(key, typeof value === "boolean" ? String(value) : value);
};

const requestMultipart = async (path, fields, files) => {
  const { baseUrl, token } = getAuthorizedBase();
  const formData = new FormData();

  Object.entries(fields || {}).forEach(([key, value]) => {
    appendFormValue(formData, key, value);
  });

  (files || []).forEach((file) => {
    if (file instanceof File || file instanceof Blob) {
      formData.append("files", file, file.name || "upload");
    }
  });

  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  return parseResponse(response);
};

const serializableChatFields = (payload, history) => {
  const {
    aiSubjectId: _ignoredAiSubjectId,
    history: _ignoredHistory,
    uploads: _ignoredUploads,
    user_id: _ignoredUserId,
    ...rest
  } = payload || {};

  return {
    ...rest,
    chat_history: history,
    history,
  };
};

const multipartChatFields = (payload, history) => {
  const fields = serializableChatFields(payload, history);
  delete fields.uploads;
  delete fields.history;

  return {
    ...fields,
    chat_history: JSON.stringify(history || []),
    persist_chat: payload?.persist_chat ?? false,
    task_memory: payload?.task_memory || "",
    message: payload?.message || "",
  };
};

const getUploads = (payload) => {
  if (!payload?.uploads) return [];
  if (Array.isArray(payload.uploads)) return payload.uploads;
  return Array.from(payload.uploads || []);
};

const ensureAiSubjectId = (payload) => {
  const aiSubjectId = payload?.aiSubjectId || payload?.subject_id;
  if (!aiSubjectId) {
    throw new Error("AI subject id fehlt.");
  }
  return aiSubjectId;
};

export const generateLessonQuiz = (payload) => {
  const aiSubjectId = ensureAiSubjectId(payload);
  const { aiSubjectId: _ignored, ...body } = payload || {};
  return requestJson(subjectPath(aiSubjectId, "generate-quiz"), body);
};

export const generateKlausurPraxis = (payload) => {
  const aiSubjectId = ensureAiSubjectId(payload);
  const { aiSubjectId: _ignored, ...body } = payload || {};
  return requestJson(subjectPath(aiSubjectId, "generate-klausur-praxis"), body);
};

export const generateSimilarAufgabe = (payload) => {
  const aiSubjectId = ensureAiSubjectId(payload);
  const { aiSubjectId: _ignored, ...body } = payload || {};
  return requestJson(subjectPath(aiSubjectId, "generate-similar-aufgabe"), body);
};

export const persistGeneratedAufgabe = (payload) => {
  const aiSubjectId = ensureAiSubjectId(payload);
  const { aiSubjectId: _ignored, ...body } = payload || {};
  return requestJson(subjectPath(aiSubjectId, "persist-generated-aufgabe"), body);
};

export const sendSubjectChat = (payload) => {
  const aiSubjectId = ensureAiSubjectId(payload);
  const history = payload?.chat_history || payload?.history || [];
  const uploads = getUploads(payload);
  const path = subjectPath(aiSubjectId, "chat");

  if (uploads.length > 0) {
    return requestMultipart(path, multipartChatFields(payload, history), uploads);
  }

  return requestJson(path, serializableChatFields(payload, history));
};
